import { put, call, takeEvery, takeLatest, delay } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import { CLIENT_TYPE, Origin } from '@upstart/harmony/lib/constants';
import {
  RECENT_DEAL_LIST_REQUEST,
  ALL_DEALS_REQUEST,
  DEAL_REQUEST,
  PAYMENTS_REQUEST,
  DEAL_CREATE_REQUEST,
  ONLINE_DEAL_CREATE_REQUEST,
  LOAD_DEAL_DATA_REQUEST,
} from './actionTypes';
import {
  recentDealListSuccess,
  recentDealListFailure,
  allDealsSuccess,
  allDealsFailure,
  dealSuccess,
  dealFailure,
  dealerSuccess,
  dealerFailure,
  paymentsSuccess,
  paymentsFailure,
  dealCreateFailure,
  dealCreateSuccess,
} from './actions';
import DealService from '../../services/DealService';
import DealerService from '../../services/DealerService';
import PaymentsService from '../../services/PaymentsService';

const POLLING_DELAY = 60000;
const DEBOUNCE_WAIT = 300;

export function* getRecentDealListTask(action) {
  const { startDate, endDate, leadSource } = action.payload;
  const fetchFn =
    leadSource === Origin.ONLINE
      ? DealService.getOnlineList
      : DealService.getShowroomList;
  try {
    const list = yield call(fetchFn, startDate, endDate);
    yield put(recentDealListSuccess(list));
  } catch (e) {
    yield put(recentDealListFailure(e));
  }
}

export function* getAllDealsTask(action) {
  const { targetServerPage, perPage, search, sort } = action.payload;
  try {
    const list = yield call(
      DealService.getAllDeals,
      targetServerPage,
      perPage,
      search,
      sort,
    );
    yield put(allDealsSuccess(list));
  } catch (error) {
    yield put(allDealsFailure(error));
  } finally {
    yield delay(POLLING_DELAY);
  }
}

export function* getDealer(dealerId) {
  try {
    const dealer = yield call(DealerService.getDealer, dealerId);
    yield put(dealerSuccess(dealer));
  } catch (error) {
    yield put(dealerFailure(error));
  }
}

export function* getDealTask(action) {
  const { dealId } = action.payload;
  try {
    const deal = yield call(DealService.getDeal, dealId);
    yield put(dealSuccess(deal));
  } catch (error) {
    yield put(dealFailure(error));
  }
}

export function* loadDealData(action) {
  try {
    const { dealId } = action.payload;
    const deal = yield call(DealService.getDeal, dealId);
    yield put(dealSuccess(deal));
    yield call(getDealer, deal.dealer_id);
  } catch (error) {
    yield put(dealFailure(error));
  }
}

export function* createDeal(action) {
  try {
    const deal = action.payload;
    const createdDeal = yield call(DealService.createDeal, deal);

    // Fetch payments to refresh fees
    yield call(
      PaymentsService.getPaymentsForPencilId,
      createdDeal.id,
      null,
      CLIENT_TYPE.IN_STORE,
    );
    yield put(dealCreateSuccess(createdDeal));
    yield put(push(`/pencil/${createdDeal.id}`));
  } catch (error) {
    yield put(dealCreateFailure(error));
  }
}

export function* createOnlineDeal(action) {
  try {
    const deal = action.payload;
    const createdDeal = yield call(
      DealService.createOnlineDeal,
      deal.dealer_id,
      deal,
    );
    yield put(dealCreateSuccess(createdDeal));
  } catch (error) {
    yield put(dealCreateFailure(error));
  }
}

export function* getPaymentsTask(action) {
  try {
    // debouncing https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
    yield delay(DEBOUNCE_WAIT);
    const { dealId, pencilId, pencil, clientType, verbose } = action.payload;
    let payments;
    // if the entire pencil is provided, send it to the backend to calculate payments
    // otherwise use the normal endpoint with generates or reads the pencil from the database
    if (pencil) {
      payments = yield call(
        PaymentsService.getPaymentsForPencilBody,
        dealId,
        pencil,
        clientType || CLIENT_TYPE.IN_STORE, // use instore payments by default
        verbose,
      );
    } else {
      payments = yield call(
        PaymentsService.getPaymentsForPencilId,
        dealId,
        pencilId,
        clientType || CLIENT_TYPE.IN_STORE, // use instore payments by default
        verbose,
      );
    }
    yield put(paymentsSuccess(payments));
  } catch (error) {
    yield put(paymentsFailure(error));
  }
}

export function* dealsSaga() {
  yield takeEvery(RECENT_DEAL_LIST_REQUEST, getRecentDealListTask);
  yield takeEvery(ALL_DEALS_REQUEST, getAllDealsTask);
  yield takeEvery(DEAL_REQUEST, getDealTask);
  yield takeLatest(PAYMENTS_REQUEST, getPaymentsTask);
  yield takeEvery(DEAL_CREATE_REQUEST, createDeal);
  yield takeEvery(ONLINE_DEAL_CREATE_REQUEST, createOnlineDeal);
  yield takeEvery(LOAD_DEAL_DATA_REQUEST, loadDealData);
}
